<template>
    <div class="profile-notification-settings">
        <UiButtonBack class="profile-notification-settings__back" @click="handleClickBack">
            Мои уведомления
        </UiButtonBack>
        <form @submit.prevent="handleFormSubmit">
            <ProfileNotificationGroup id="site_notification"
                                      class="profile-notification-settings__group"
                                      title="Настройки уведомлений на сайте"
            >
                <template #field>
                    <ul class="profile-notification-settings__list">
                        <li>
                            <WBCheckbox v-model="notifications.siteNotification.techWorks">
                                <template #text>
                                    Получать уведомления о технических работах на сайте
                                </template>
                            </WBCheckbox>
                        </li>
                        <li>
                            <WBCheckbox v-model="notifications.siteNotification.newBlogArticle">
                                <template #text>
                                    Получать уведомления о новых статьях
                                </template>
                            </WBCheckbox>
                        </li>
                    </ul>

                    <WBToggle v-model="notifications.siteNotification.subscriptionEndWarning"
                              class="profile-notification-settings__toggle"
                    >
                        <template #label>
                            Получать напоминания об окончании действия подписки
                        </template>
                    </WBToggle>

                    <div class="profile-notification-settings__time">
                        <div class="profile-notification-settings__time-title">
                            Получать напоминания:
                        </div>

                        <WBRadioGroup v-model="notifications.siteNotification.subscriptionEndWarningDays"
                                      class="profile-notification-settings__radio-group"
                                      name="time"
                                      :disabled="!notifications.siteNotification.subscriptionEndWarning"
                                      :options="options"
                        />
                    </div>
                </template>
            </ProfileNotificationGroup>
            <WBDivider class="profile-notification-settings__divider" />
            <ProfileNotificationGroup id="email_notification"
                                      class="profile-notification-settings__group"
                                      title="Настройки уведомлений на почту"
            >
                <template #field>
                    <ul class="profile-notification-settings__list">
                        <li>
                            <WBCheckbox v-model="notifications.emailNotification.promotion">
                                <template #text>
                                    Получать информационные рассылки с акциями
                                </template>
                            </WBCheckbox>
                        </li>
                        <li>
                            <WBCheckbox v-model="notifications.emailNotification.newBlogArticle">
                                <template #text>
                                    Получать рассылку о новых статьях
                                </template>
                            </WBCheckbox>
                        </li>
                    </ul>

                    <WBToggle v-model="notifications.emailNotification.subscriptionEndWarning"
                              class="profile-notification-settings__toggle"
                    >
                        <template #label>
                            Получать напоминания об окончании действия подписки
                        </template>
                    </WBToggle>

                    <div class="profile-notification-settings__time">
                        <div class="profile-notification-settings__time-title">
                            Получать напоминания:
                        </div>

                        <WBRadioGroup v-model="notifications.emailNotification.subscriptionEndWarningDays"
                                      class="profile-notification-settings__radio-group"
                                      name="time"
                                      :disabled="!notifications.emailNotification?.subscriptionEndWarning"
                                      :options="options"
                        />
                    </div>
                </template>
            </ProfileNotificationGroup>
            <template v-if="isChanged">
                <WBDivider class="profile-notification-settings__divider" />
                <UiButton :disabled="isRequestInProgress"
                          :loading="isRequestInProgress"
                          color="primary"
                          type="submit"
                >
                    Сохранить
                </UiButton>
            </template>
        </form>
    </div>
</template>

<script setup lang="ts">
    import type { UpdateUserRequest } from '~/src/shared/types/generated'
    import cloneDeep from 'lodash/cloneDeep'
    import isEqual from 'lodash/isEqual'
    import ProfileNotificationGroup
        from '~/src/shared/components/pages/profile/ProfileNotification/components/ProfileNotificationGroup/ProfileNotificationGroup.vue'
    import { defaultNotificationSettings } from '~/src/shared/components/pages/profile/ProfileNotification/page/types'
    import { useUserStore } from '~/src/shared/pinia/user/useUserStore'

    const options = [
        {
            label: 'за 1 день до окончания подписки',
            value: 1,
        },
        {
            label: 'за 3 дня до окончания подписки',
            value: 3,
        },
        {
            label: 'за неделю до окончания подписки',
            value: 7,
        },
    ]
    const isRequestInProgress = ref<boolean>(false)
    const userStore = useUserStore()
    const { updateUserInfo } = userStore
    const user = computed(() => userStore.user)

    // const userNotifications = computed() => user?.userNotificationSettings)

    const notifications = ref({ ...user.value?.userNotificationSettings || defaultNotificationSettings })
    const currentSettings = cloneDeep(notifications.value)
    const isChanged = ref<boolean>(false)

    watch(notifications, (newValue) => {
        isChanged.value = !isEqual(newValue, currentSettings)
    }, { deep: true })

    async function handleFormSubmit() {
        const data: UpdateUserRequest = {
            firstName: user.value?.firstName,
            lastName: user.value?.lastName,
            phone: user.value?.phone,
            email: user.value?.email,
            userNotificationSettings: {
                ...notifications.value,
            },
        } as UpdateUserRequest

        try {
            isRequestInProgress.value = true
            await updateUserInfo(data)
        } catch (_e) {
            const e = _e as Error
            console.warn(`NotificationProfilePage/handleClickButtonSave: ${e}`)
        } finally {
            isChanged.value = false
            isRequestInProgress.value = false
        }
    }

    async function handleClickBack() {
        await navigateTo('/profile/notification')
    }
</script>

<style scoped lang="scss">
    .profile-notification-settings {
        padding-top: 24px;

        &__back {
            position: absolute;
            top: 24px;
        }

        &__divider {
            margin: 20px 0;
        }

        &__toggle {
            margin-top: 24px;
        }

        &__list {
            li {
                &:not(:first-child) {
                    margin-top: 12px;
                }
            }
        }

        &__time {
            margin-top: 24px;
        }

        &__time-title {
            margin-bottom: 16px;
            font-size: 16px;
            font-weight: 500;
            line-height: 1.4;
        }

        &__radio-group {
            margin-top: 16px;
        }
    }
</style>
